import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ShieldCheck, 
  CheckCircle2, 
  Clock,
  Phone,
  ArrowLeft,
  Users,
  Award,
  UserPlus,
  RefreshCw,
  Building,
  FileCheck
} from "lucide-react";

const courseDetails = {
  duration: "2-4 שעות",
  certification: "תעודת הדרכה",
  participants: "עד 30 משתתפים",
  location: "באתר הלקוח"
};

const trainingTypes = [
  {
    icon: UserPlus,
    title: "הדרכת כניסה לעבודה",
    duration: "2 שעות",
    description: "הדרכה חובה לכל עובד חדש לפני תחילת העבודה",
    topics: [
      "הכרת סיכוני העבודה",
      "נהלי בטיחות בארגון",
      "שימוש בציוד מגן",
      "נהלי חירום ופינוי"
    ]
  },
  {
    icon: RefreshCw,
    title: "הדרכה תקופתית",
    duration: "2 שעות",
    description: "ריענון שנתי לכל העובדים כנדרש בחוק",
    topics: [
      "עדכונים בנהלי בטיחות",
      "הפקת לקחים מאירועים",
      "ריענון נושאי ליבה",
      "שינויים ברגולציה"
    ]
  },
  {
    icon: Building,
    title: "הדרכה לפי תפקיד",
    duration: "3-4 שעות",
    description: "הדרכה ייעודית לבעלי תפקידים ספציפיים",
    topics: [
      "סיכונים ייחודיים לתפקיד",
      "ציוד ונהלים מיוחדים",
      "אחריות בטיחותית",
      "דיווח ותיעוד"
    ]
  }
];

const legalRequirements = [
  {
    title: "הדרכת כניסה",
    requirement: "חובה לכל עובד חדש לפני תחילת העבודה"
  },
  {
    title: "הדרכה תקופתית",
    requirement: "חובה לכל עובד לפחות אחת לשנה"
  },
  {
    title: "תיעוד",
    requirement: "חובת תיעוד ההדרכה בתיק הבטיחות"
  },
  {
    title: "שפות",
    requirement: "ההדרכה חייבת להיות בשפה שהעובד מבין"
  }
];

const topics = [
  "זכויות וחובות בטיחות",
  "זיהוי סיכונים במקום העבודה",
  "שימוש נכון בציוד מגן אישי",
  "ארגונומיה ותנוחות עבודה",
  "בטיחות חשמל",
  "בטיחות אש ופינוי",
  "עזרה ראשונה בסיסית",
  "דיווח על מפגעים ותאונות"
];

const faqs = [
  {
    question: "האם חייבים לבצע הדרכת בטיחות לכל עובד?",
    answer: "כן, על פי חוק כל עובד חייב לעבור הדרכת בטיחות לפני תחילת העבודה (הדרכת כניסה) ולאחר מכן הדרכה תקופתית לפחות אחת לשנה."
  },
  {
    question: "האם ניתן לקיים את ההדרכה באופן מקוון?",
    answer: "חלק מההדרכה יכול להתבצע באופן מקוון, אך רכיבים מעשיים כמו שימוש במטפים או ציוד מגן חייבים להתבצע פרונטלית."
  },
  {
    question: "מה קורה אם לא מבצעים הדרכות?",
    answer: "אי ביצוע הדרכות הוא עבירה על החוק שעלולה לגרור קנסות, ובמקרה של תאונה - אחריות משפטית מוגברת."
  },
  {
    question: "האם אתם מספקים תיעוד של ההדרכה?",
    answer: "כן, אנו מספקים את כל התיעוד הנדרש כולל רשימות נוכחות, תכני ההדרכה ותעודות לעובדים."
  }
];

export default function WorkplaceSafety() {
  return (
    <Layout>
      <Helmet>
        <title>הדרכת בטיחות בעבודה | Adam Safety</title>
        <meta name="description" content="הדרכות בטיחות בעבודה לארגונים - הדרכת כניסה לעבודה, הדרכה תקופתית והדרכות ייעודיות. עמידה בדרישות החוק עם תיעוד מלא." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <ShieldCheck className="w-5 h-5" />
              <span className="text-sm font-medium">הדרכת בטיחות</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              הדרכת בטיחות בעבודה
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              הדרכות בטיחות מקצועיות לעמידה בדרישות החוק ושמירה על העובדים - 
              הדרכות כניסה, תקופתיות וייעודיות
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Award className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.certification}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Users className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.participants}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הזמן הדרכה
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Training Types */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              סוגי ההדרכות
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {trainingTypes.map((training, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="icon-container">
                    <training.icon className="w-6 h-6" />
                  </div>
                  <span className="text-sm px-3 py-1 rounded-full bg-accent/20 text-accent">
                    {training.duration}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{training.title}</h3>
                <p className="text-muted-foreground text-sm mb-4">{training.description}</p>
                <ul className="space-y-2">
                  {training.topics.map((topic, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">{topic}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Topics Covered */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              נושאים נלמדים
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {topics.map((topic, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-2 bg-card px-4 py-2 rounded-full border border-border shadow-sm"
              >
                <CheckCircle2 className="w-4 h-4 text-accent" />
                <span className="text-foreground text-sm">{topic}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Requirements */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              דרישות החוק
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {legalRequirements.map((req, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary rounded-2xl p-6 text-primary-foreground"
              >
                <FileCheck className="w-8 h-8 text-accent mb-3" />
                <h3 className="font-bold mb-2">{req.title}</h3>
                <p className="text-primary-foreground/80 text-sm">{req.requirement}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות נפוצות
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              הגיע הזמן לחדש הדרכות?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              נגיע אליכם ונדאג שכל העובדים יהיו מעודכנים
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הזמנת הדרכה
                </Button>
              </Link>
              <Link to="/trainings">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  הדרכות נוספות
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}